import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../api/axios';
import { lostReasonGroup, lostReasonLabel } from '../constants/lostReasons';
import { AGED_STATUSES, STALE_AFTER_DAYS, isStalling, stageDays } from '../constants/cartAging';
import './CartAnalytics.css';

const fmt = (n) =>
    new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 })
        .format(n || 0);

const pct = (part, whole) => (whole > 0 ? Math.round((part / whole) * 100) : 0);

const PAGE_SIZE = 10;

/** The group whose share is worth managing — the reasons that are our own doing. */
const OWN_DOING = 'Our execution';

/**
 * The report shown. First entry is also the default.
 *
 * Lost leads: it is the question this screen was built to answer. Stalling sits
 * second but carries a count badge, so it still announces itself without having
 * to be the landing view.
 *
 * Each report answers a different question and so carries its own controls — the
 * period filter belongs to Lost alone, since aging is a snapshot of right now and
 * cannot be scoped to a past window.
 *
 * Adding a report is a row here plus a branch in the body.
 */
const VIEWS = [
    { key: 'lost',     label: 'Lost carts' },
    { key: 'stalling', label: 'Stalling carts' },
];

const PERIODS = [
    { key: 'all',    label: 'All time' },
    { key: 'today',  label: 'Today' },
    { key: 'week',   label: 'This week' },
    { key: 'month',  label: 'This month' },
    { key: 'year',   label: 'This year' },
    { key: 'custom', label: 'Custom' },
];

const startOfDay = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };

/**
 * Resolve a preset to [start, end]; null on either side means unbounded.
 * Weeks start Monday — the business week here, and the day losses cluster on.
 */
const rangeFor = (period, from, to) => {
    const now = new Date();
    switch (period) {
        case 'today':
            return [startOfDay(now), null];
        case 'week': {
            const d = startOfDay(now);
            d.setDate(d.getDate() - ((d.getDay() + 6) % 7));
            return [d, null];
        }
        case 'month':
            return [new Date(now.getFullYear(), now.getMonth(), 1), null];
        case 'year':
            return [new Date(now.getFullYear(), 0, 1), null];
        case 'custom':
            return [from ? startOfDay(from) : null, to ? new Date(`${to}T23:59:59`) : null];
        default:
            return [null, null];
    }
};

/**
 * When a cart was settled — won or lost.
 *
 * A settled-cart report has to be dated by when it was *settled*, not when it was
 * created — a cart opened in March and closed in August belongs in August, and
 * dating it by createdAt would file it under a month in which nothing happened.
 *
 * convertedAt first for won carts: it survives a later move out of CONVERTED,
 * where statusChangedAt would be overwritten. Then statusChangedAt, which is the
 * exact moment for everything else but is NULL on every cart that has not changed
 * status since the column was added — updatedAt stands in for those. That is a
 * proxy rather than a guess, since settling a cart writes it immediately
 * beforehand, but any later edit moves it. Hence `exact`: the screen reports how
 * many of the carts on it are approximate rather than quietly mixing the two.
 */
const settledAt = (q) => {
    if (q.status === 'CONVERTED' && q.convertedAt) {
        return { date: new Date(q.convertedAt), exact: true };
    }
    if (q.statusChangedAt) return { date: new Date(q.statusChangedAt), exact: true };
    return { date: q.updatedAt ? new Date(q.updatedAt) : null, exact: false };
};

/** Is this cart inside the active window? Unbounded range accepts everything. */
const inRange = (q, start, end) => {
    if (!start && !end) return true;
    const { date } = settledAt(q);
    if (!date) return false;
    if (start && date < start) return false;
    if (end && date > end) return false;
    return true;
};

const dateLabel = (d) =>
    d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });

/**
 * Lost-cart analytics for admins.
 *
 * Reads the same GET /quotes/all the curated-carts screen uses rather than a new
 * aggregate endpoint: an ADMIN already receives every cart there, the volume is
 * in the hundreds, and keeping one source means these figures cannot drift from
 * the ones on the carts screen.
 */
const CartAnalytics = () => {
    const [quotes, setQuotes] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');

    // Held in the URL, matching how /admin drives its own views: a report someone
    // is looking at should survive a refresh and be sendable to a colleague.
    // replace:true so switching reports doesn't stack up back-button history.
    const [searchParams, setSearchParams] = useSearchParams();
    const viewParam = searchParams.get('view');
    const view = VIEWS.some(v => v.key === viewParam) ? viewParam : VIEWS[0].key;
    const setView = (key) => {
        const next = new URLSearchParams(searchParams);
        next.set('view', key);
        setSearchParams(next, { replace: true });
    };

    // Two independent drill-downs that intersect. Either can be set alone; setting
    // both answers "what did this person lose, for this reason".
    const [reasonFilter, setReasonFilter] = useState('');
    const [employeeFilter, setEmployeeFilter] = useState('');
    const [page, setPage] = useState(0);

    // Defaults to all time rather than the conventional this-month: reason capture
    // only just started, so a month-scoped first visit would show an empty screen
    // and read as broken rather than as new.
    const [period, setPeriod] = useState('all');
    const [customFrom, setCustomFrom] = useState('');
    const [customTo, setCustomTo] = useState('');

    useEffect(() => {
        api.get('/quotes/all')
            .then(res => { setQuotes(res.data || []); setIsLoading(false); })
            .catch(() => { setError('Could not load cart analytics.'); setIsLoading(false); });
    }, []);

    // Any change of filter should return to the first page, or the table can land
    // on a page that no longer exists and render empty.
    useEffect(() => { setPage(0); }, [reasonFilter, employeeFilter, period, customFrom, customTo]);

    const [rangeStart, rangeEnd] = useMemo(
        () => rangeFor(period, customFrom, customTo),
        [period, customFrom, customTo]
    );

    const stats = useMemo(() => {
        let approximate = 0;

        const lost = quotes.filter(q => {
            if (q.status !== 'CLOSED_LOST') return false;
            if (!inRange(q, rangeStart, rangeEnd)) return false;
            if (!settledAt(q).exact) approximate++;
            return true;
        });

        // Won carts in the same window, for the denominator. Without them the screen
        // can only ever report that losses are large; a rate says whether that is bad.
        let wonValue = 0;
        let wonCount = 0;
        quotes.forEach(q => {
            if (q.status !== 'CONVERTED') return;
            if (!inRange(q, rangeStart, rangeEnd)) return;
            wonCount++;
            wonValue += q.totalValue || 0;
        });


        const byReason = {};
        const byEmployee = {};
        let totalValue = 0;
        let attributedValue = 0;   // value of carts that carry a reason
        let ownDoingValue = 0;
        let withReason = 0;

        lost.forEach(q => {
            const value = q.totalValue || 0;
            totalValue += value;

            const reason = q.lostReason || '';
            if (reason) {
                withReason++;
                attributedValue += value;
                if (lostReasonGroup(reason) === OWN_DOING) ownDoingValue += value;

                const r = byReason[reason] || { count: 0, value: 0 };
                r.count++; r.value += value;
                byReason[reason] = r;
            }

            // Strictly the recorded closer. Falling back to assignedScmEmail or
            // createdByEmail here would attribute losses to customers on any
            // customer-submitted RFQ, and to unvalidated free text elsewhere.
            const who = q.lostByEmail || '';
            const e = byEmployee[who] || { count: 0, value: 0, reasons: {} };
            e.count++; e.value += value;
            if (reason) e.reasons[reason] = (e.reasons[reason] || 0) + 1;
            byEmployee[who] = e;
        });

        // Grouped for display, dropping groups nobody has used yet.
        const groups = {};
        Object.entries(byReason).forEach(([reason, tally]) => {
            const name = lostReasonGroup(reason);
            const g = groups[name] || { count: 0, value: 0, rows: [] };
            g.count += tally.count; g.value += tally.value;
            g.rows.push({ reason, ...tally });
            groups[name] = g;
        });
        Object.values(groups).forEach(g => g.rows.sort((a, b) => b.value - a.value));

        return {
            lost,
            approximate,
            wonValue,
            wonCount,
            settledValue: totalValue + wonValue,
            totalValue,
            attributedValue,
            ownDoingValue,
            withReason,
            byReason,
            byEmployee,
            groups: Object.entries(groups).sort((a, b) => b[1].value - a[1].value),
        };
    }, [quotes, rangeStart, rangeEnd]);

    /**
     * Open carts going stale, by stage.
     *
     * Deliberately outside the period control. Aging is a snapshot of what is
     * sitting there right now; scoping it to "this month" would hide precisely the
     * carts that have been rotting longest, which are the ones worth seeing.
     */
    const aging = useMemo(() => {
        const now = Date.now();
        const rows = AGED_STATUSES.map(status => {
            const inStage = quotes.filter(q => q.status === status);
            const stale = inStage
                .filter(q => isStalling(q, now))
                .sort((a, b) => (stageDays(b, now) || 0) - (stageDays(a, now) || 0));
            return {
                status,
                threshold: STALE_AFTER_DAYS[status],
                total: inStage.length,
                totalValue: inStage.reduce((s, q) => s + (q.totalValue || 0), 0),
                stale,
                staleValue: stale.reduce((s, q) => s + (q.totalValue || 0), 0),
                oldest: stale.length ? stageDays(stale[0], now) : null,
            };
        }).filter(r => r.total > 0);

        return {
            rows,
            openCount: rows.reduce((s, r) => s + r.total, 0),
            openValue: rows.reduce((s, r) => s + r.totalValue, 0),
            staleCount: rows.reduce((s, r) => s + r.stale.length, 0),
            staleValue: rows.reduce((s, r) => s + r.staleValue, 0),
            worst: rows
                .flatMap(r => r.stale.map(q => ({ q, status: r.status })))
                .sort((a, b) => (stageDays(b.q, now) || 0) - (stageDays(a.q, now) || 0))
                .slice(0, 10),
        };
    }, [quotes]);

    const drilled = useMemo(() => {
        return stats.lost.filter(q =>
            (!reasonFilter || q.lostReason === reasonFilter) &&
            (!employeeFilter || (q.lostByEmail || '') === employeeFilter)
        ).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    }, [stats.lost, reasonFilter, employeeFilter]);

    const pageCount = Math.max(1, Math.ceil(drilled.length / PAGE_SIZE));
    const paged = drilled.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE);

    const toggle = (setter, current) => (next) => setter(current === next ? '' : next);
    const onReason = toggle(setReasonFilter, reasonFilter);
    const onEmployee = toggle(setEmployeeFilter, employeeFilter);

    if (isLoading) return <div className="loading-state">Loading cart analytics…</div>;
    if (error) return <div className="ql-error">{error}</div>;

    const employees = Object.entries(stats.byEmployee)
        .sort((a, b) => b[1].value - a[1].value);

    return (
        <div className="ca">
            <div className="ca__head">
                <h2 className="ca__title">Cart Analytics</h2>
                <p className="ca__subtitle">
                    {view === 'stalling'
                        ? 'Open carts sitting too long in a stage, and who owns them.'
                        : 'Why curated carts are being lost, and who is closing them.'}
                </p>
            </div>

            <nav className="ca-tabs" role="tablist" aria-label="Report">
                {VIEWS.map(v => (
                    <button
                        key={v.key}
                        role="tab"
                        aria-selected={view === v.key}
                        className={`ca-tab${view === v.key ? ' ca-tab--on' : ''}`}
                        onClick={() => setView(v.key)}
                    >
                        {v.label}
                        {v.key === 'stalling' && aging.staleCount > 0 && (
                            <span className="ca-tab__badge">{aging.staleCount}</span>
                        )}
                    </button>
                ))}
            </nav>

            {view === 'stalling' && (aging.rows.length === 0 ? (
                <div className="ca__empty">No open carts to age.</div>
            ) : (
                <>
                    <div className="ca__kpis">
                        <div className="ca-kpi ca-kpi--warn">
                            <div className="ca-kpi__value">{fmt(aging.staleValue)}</div>
                            <div className="ca-kpi__label">Value stalling</div>
                            <div className="ca-kpi__sub">{aging.staleCount} carts past threshold</div>
                        </div>
                        <div className="ca-kpi">
                            {/* The ratio that matters: an absolute figure can't say whether
                                this is a healthy pipeline with a few stragglers or a stuck one. */}
                            <div className={`ca-kpi__value${aging.openValue > 0 ? '' : ' ca-kpi__value--none'}`}>
                                {aging.openValue > 0
                                    ? `${pct(aging.staleValue, aging.openValue)}%`
                                    : 'No data yet'}
                            </div>
                            <div className="ca-kpi__label">Of open pipeline value</div>
                            <div className="ca-kpi__sub">
                                {fmt(aging.staleValue)} of {fmt(aging.openValue)} across {aging.openCount} open carts
                            </div>
                        </div>
                        <div className="ca-kpi">
                            <div className={`ca-kpi__value${aging.worst.length ? '' : ' ca-kpi__value--none'}`}>
                                {aging.worst.length ? `${stageDays(aging.worst[0].q)}d` : 'Nothing stale'}
                            </div>
                            <div className="ca-kpi__label">Oldest</div>
                            <div className="ca-kpi__sub">
                                {aging.worst.length ? `in ${aging.worst[0].status}` : 'every cart within threshold'}
                            </div>
                        </div>
                    </div>

                    <section className="ca-stall">
                        <div className="ca-stall__head">
                            <div>
                                <h3 className="ca-stall__title">By stage</h3>
                                <p className="ca-stall__sub">
                                    A snapshot as of today. Thresholds are per stage — 14 days for
                                    requested, cart and quote; 30 for on-hold.
                                </p>
                            </div>
                        </div>

                        <div className="ca-stall__stages">
                        {aging.rows.map(r => (
                            <div key={r.status} className="ca-stall__stage">
                                <span className="ca-stall__stage-name">{r.status}</span>
                                <span className={`ca-stall__stage-count${r.stale.length ? ' ca-stall__stage-count--bad' : ''}`}>
                                    {r.stale.length} of {r.total} past {r.threshold}d
                                </span>
                                <span className="ca-stall__stage-val">
                                    {r.stale.length ? fmt(r.staleValue) : '—'}
                                </span>
                                <span className="ca-stall__stage-age">
                                    {r.oldest != null ? `oldest ${r.oldest}d` : 'all fresh'}
                                </span>
                            </div>
                        ))}
                    </div>

                    {aging.worst.length > 0 && (
                        <table className="ca-table ca-stall__table">
                            <thead>
                                <tr>
                                    <th>Oldest stalling carts</th>
                                    <th>Stage</th>
                                    <th>Age</th>
                                    <th>Value</th>
                                    <th>Owner</th>
                                </tr>
                            </thead>
                            <tbody>
                                {aging.worst.map(({ q, status }) => (
                                    <tr key={q.id}>
                                        <td>{q.cartName}</td>
                                        <td>{status}</td>
                                        <td>{stageDays(q)}d</td>
                                        <td>{fmt(q.totalValue)}</td>
                                        <td className={q.assignedScmEmail ? '' : 'ca-muted'}>
                                            {q.assignedScmEmail || q.createdByEmail || 'Unassigned'}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                    </section>
                </>
            ))}

            {view === 'lost' && (
            <>
            <div className="ca__period">
                <div className="ca-periods" role="group" aria-label="Time period">
                    {PERIODS.map(p => (
                        <button
                            key={p.key}
                            className={`ca-period${period === p.key ? ' ca-period--on' : ''}`}
                            onClick={() => setPeriod(p.key)}
                            aria-pressed={period === p.key}
                        >
                            {p.label}
                        </button>
                    ))}
                </div>

                {period === 'custom' && (
                    <div className="ca-range">
                        <label>
                            From
                            <input type="date" value={customFrom} max={customTo || undefined}
                                   onChange={e => setCustomFrom(e.target.value)} />
                        </label>
                        <label>
                            To
                            <input type="date" value={customTo} min={customFrom || undefined}
                                   onChange={e => setCustomTo(e.target.value)} />
                        </label>
                    </div>
                )}

                {/* Say plainly what window is on screen. A period control that only
                    highlights a pill leaves the reader guessing where it starts. */}
                <div className="ca__range-label">
                    {rangeStart || rangeEnd
                        ? `Carts lost ${rangeStart ? dateLabel(rangeStart) : 'any time'} – ${rangeEnd ? dateLabel(rangeEnd) : 'today'}`
                        : 'Carts lost at any time'}
                </div>
            </div>

            {stats.lost.length === 0 ? (
                <div className="ca__empty">
                    {rangeStart || rangeEnd
                        ? 'No carts were lost in this period.'
                        : 'No closed-lost carts yet.'}
                </div>
            ) : (
                <>
                    {/* Three tiles, not eight. A number earns a tile when it is a ratio
                        you can act on and watch move; raw counts belong in the tables
                        below, where they can be compared against something. */}
                    <div className="ca__kpis">
                        {/* The anchor is a rate, not a total. "₹17,700 lost" can only ever
                            say losses are large; against what was won in the same window it
                            says whether that is bad. Value-weighted rather than by count
                            because the two diverge sharply here — big deals are lost and
                            small ones won — and the value answer is the honest one. */}
                        <div className="ca-kpi">
                            <div className={`ca-kpi__value${stats.settledValue > 0 ? '' : ' ca-kpi__value--none'}`}>
                                {stats.settledValue > 0
                                    ? `${pct(stats.totalValue, stats.settledValue)}%`
                                    : 'No data yet'}
                            </div>
                            <div className="ca-kpi__label">Loss rate by value</div>
                            <div className="ca-kpi__sub">
                                {fmt(stats.totalValue)} lost of {fmt(stats.settledValue)} settled
                            </div>
                            <div className="ca-kpi__sub">
                                {stats.lost.length} lost · {stats.wonCount} won
                                {stats.lost.length + stats.wonCount > 0 && (
                                    <> · {pct(stats.lost.length, stats.lost.length + stats.wonCount)}% by count</>
                                )}
                            </div>
                        </div>

                        <div className="ca-kpi ca-kpi--warn">
                            <div className={`ca-kpi__value${stats.attributedValue > 0 ? '' : ' ca-kpi__value--none'}`}>
                                {stats.attributedValue > 0
                                    ? `${pct(stats.ownDoingValue, stats.attributedValue)}%`
                                    : 'No data yet'}
                            </div>
                            <div className="ca-kpi__label">Lost to our own execution</div>
                            {/* Measured against carts that carry a reason, not all lost
                                carts. Using every cart as the denominator would bury this
                                under the historical blanks and read as near-zero forever. */}
                            <div className="ca-kpi__sub">
                                {fmt(stats.ownDoingValue)} of {fmt(stats.attributedValue)} with a reason
                            </div>
                        </div>

                        <div className="ca-kpi">
                            <div className="ca-kpi__value">{pct(stats.withReason, stats.lost.length)}%</div>
                            <div className="ca-kpi__label">Reason coverage</div>
                            <div className="ca-kpi__sub">
                                {stats.withReason} of {stats.lost.length} carts
                            </div>
                        </div>
                    </div>

                    {stats.approximate > 0 && (
                        <div className="ca__notice ca__notice--soft">
                            {stats.approximate} of these {stats.lost.length} carts are dated by
                            last edit rather than close time — they were closed before close-time
                            tracking began on 14 Aug 2026, so their period is approximate.
                        </div>
                    )}

                    {stats.withReason === 0 && (
                        <div className="ca__notice">
                            No reasons recorded yet. Reasons are captured from the moment a cart is
                            closed as lost, so these charts fill in going forward — the{' '}
                            {stats.lost.length} existing carts were closed before it was asked for.
                        </div>
                    )}

                    <div className="ca__cols">
                        {/* ── Reasons ── */}
                        <section className="ca-panel">
                            <div className="ca-panel__head">
                                <h3 className="ca-panel__title">Why they were lost</h3>
                                {reasonFilter && (
                                    <button className="ca-clear" onClick={() => setReasonFilter('')}>
                                        Clear ✕
                                    </button>
                                )}
                            </div>

                            {stats.groups.length === 0 ? (
                                <div className="ca-panel__empty">Nothing to show yet.</div>
                            ) : stats.groups.map(([name, group]) => (
                                <div key={name} className="ca-group">
                                    <div className="ca-group__head">
                                        <span className={`ca-group__name${name === OWN_DOING ? ' ca-group__name--own' : ''}`}>
                                            {name}
                                        </span>
                                        <span className="ca-group__total">
                                            {group.count} · {fmt(group.value)}
                                        </span>
                                    </div>
                                    {group.rows.map(row => (
                                        <button
                                            key={row.reason}
                                            className={`ca-bar${reasonFilter === row.reason ? ' ca-bar--on' : ''}`}
                                            onClick={() => onReason(row.reason)}
                                            aria-pressed={reasonFilter === row.reason}
                                        >
                                            <span className="ca-bar__label">{lostReasonLabel(row.reason)}</span>
                                            <span className="ca-bar__track">
                                                <span
                                                    className="ca-bar__fill"
                                                    style={{ width: `${pct(row.value, stats.attributedValue)}%` }}
                                                />
                                            </span>
                                            <span className="ca-bar__val">{row.count} · {fmt(row.value)}</span>
                                        </button>
                                    ))}
                                </div>
                            ))}
                        </section>

                        {/* ── Employees ── */}
                        <section className="ca-panel">
                            <div className="ca-panel__head">
                                <h3 className="ca-panel__title">Who closed them</h3>
                                {employeeFilter && (
                                    <button className="ca-clear" onClick={() => setEmployeeFilter('')}>
                                        Clear ✕
                                    </button>
                                )}
                            </div>

                            <table className="ca-table">
                                <thead>
                                    <tr>
                                        <th>Closed by</th>
                                        <th>Carts</th>
                                        <th>Value</th>
                                        <th>Most common reason</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {employees.map(([email, e]) => {
                                        const top = Object.entries(e.reasons)
                                            .sort((a, b) => b[1] - a[1])[0];
                                        return (
                                            <tr
                                                key={email || 'UNATTRIBUTED'}
                                                className={`ca-table__row${employeeFilter === email ? ' ca-table__row--on' : ''}`}
                                                onClick={() => onEmployee(email)}
                                            >
                                                <td className={email ? '' : 'ca-muted'}>
                                                    {email || 'Not attributed'}
                                                </td>
                                                <td>{e.count}</td>
                                                <td>{fmt(e.value)}</td>
                                                <td className={top ? '' : 'ca-muted'}>
                                                    {top ? `${lostReasonLabel(top[0])} (${top[1]})` : '—'}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                            {stats.byEmployee[''] && (
                                <p className="ca-panel__note">
                                    “Not attributed” is every cart closed before the closer was
                                    recorded. It is not a person, and it will not grow.
                                </p>
                            )}
                        </section>
                    </div>

                    {/* ── Drill-down ── */}
                    <section className="ca-panel ca-panel--wide">
                        <div className="ca-panel__head">
                            <h3 className="ca-panel__title">
                                {reasonFilter || employeeFilter ? 'Matching carts' : 'All lost carts'}
                                <span className="ca-count"> · {drilled.length}</span>
                            </h3>
                            {(reasonFilter || employeeFilter) && (
                                <button
                                    className="ca-clear"
                                    onClick={() => { setReasonFilter(''); setEmployeeFilter(''); }}
                                >
                                    Clear all filters ✕
                                </button>
                            )}
                        </div>

                        {(reasonFilter || employeeFilter) && (
                            <div className="ca-chips">
                                {reasonFilter && (
                                    <button className="ca-chip" onClick={() => setReasonFilter('')}>
                                        {lostReasonLabel(reasonFilter)} ✕
                                    </button>
                                )}
                                {employeeFilter && (
                                    <button className="ca-chip" onClick={() => setEmployeeFilter('')}>
                                        {employeeFilter} ✕
                                    </button>
                                )}
                            </div>
                        )}

                        <table className="ca-table">
                            <thead>
                                <tr>
                                    <th>Cart</th>
                                    <th>Reason</th>
                                    <th>Closed by</th>
                                    <th>Value</th>
                                    <th>Lost</th>
                                </tr>
                            </thead>
                            <tbody>
                                {paged.length === 0 ? (
                                    <tr><td colSpan="5" className="ca-muted ca-center">No carts match.</td></tr>
                                ) : paged.map(q => (
                                    <tr key={q.id}>
                                        <td>{q.cartName}</td>
                                        <td className={q.lostReason ? '' : 'ca-muted'} title={q.lostReasonNote || undefined}>
                                            {q.lostReason ? lostReasonLabel(q.lostReason) : 'Not recorded'}
                                        </td>
                                        <td className={q.lostByEmail ? '' : 'ca-muted'}>
                                            {q.lostByEmail || 'Not attributed'}
                                        </td>
                                        <td>{fmt(q.totalValue)}</td>
                                        {(() => {
                                            const { date, exact } = settledAt(q);
                                            return (
                                                <td
                                                    className={exact ? '' : 'ca-approx'}
                                                    title={exact ? undefined : 'Approximate — dated by last edit'}
                                                >
                                                    {date ? date.toLocaleDateString('en-IN') : '—'}
                                                    {date && !exact && ' ~'}
                                                </td>
                                            );
                                        })()}
                                    </tr>
                                ))}
                            </tbody>
                        </table>

                        {pageCount > 1 && (
                            <div className="ca-pager">
                                <button disabled={page === 0} onClick={() => setPage(p => p - 1)}>‹ Prev</button>
                                <span>Page {page + 1} of {pageCount}</span>
                                <button disabled={page >= pageCount - 1} onClick={() => setPage(p => p + 1)}>Next ›</button>
                            </div>
                        )}
                    </section>
                </>
            )}
            </>
            )}
        </div>
    );
};

export default CartAnalytics;
